import { useEffect, useState } from "react";
import { carriesNode } from "../lib/dragMove.ts";
import {
  batchTarget,
  carriesFiles,
  getDropState,
  setHoverTarget,
  useDropState,
} from "../lib/drop.ts";
import { holdMood } from "../lib/mood.ts";
import { enqueue } from "../lib/uploads.ts";
import styles from "./DropLayer.module.css";
import { Icon } from "./Icon.tsx";

export function DropLayer() {
  const [dragging, setDragging] = useState(false);
  const [count, setCount] = useState(0);
  const { pageTarget, folder, hoverTarget, hoverFolder } = useDropState();

  useEffect(() => {
    let depth = 0;
    const reset = () => {
      depth = 0;
      setDragging(false);
      setHoverTarget(null);
    };
    const onEnter = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth += 1;
      setCount(event.dataTransfer?.items.length ?? 0);
      setDragging(true);
    };
    const onLeave = (event: DragEvent) => {
      if (!carriesFiles(event)) return;
      depth -= 1;
      if (depth <= 0) reset();
    };
    const onOver = (event: DragEvent) => {
      if (carriesNode(event)) {
        // A row let go outside any folder stays put instead of opening as a link.
        event.preventDefault();
        if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
        return;
      }
      if (!carriesFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
      setHoverTarget(null);
    };
    const onDrop = (event: DragEvent) => {
      if (carriesNode(event)) {
        event.preventDefault();
        return;
      }
      if (!carriesFiles(event)) return;
      event.preventDefault();
      const files = [...(event.dataTransfer?.files ?? [])];
      const { pageTarget: target } = getDropState();
      reset();
      enqueue(files, files.length === 1 ? target : batchTarget(target));
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    window.addEventListener("dragend", reset);
    const onReset = () => reset();
    window.addEventListener("drive:drop-handled", onReset);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drive:drop-handled", onReset);
    };
  }, []);

  useEffect(() => {
    holdMood(dragging ? "alert" : null);
    return () => holdMood(null);
  }, [dragging]);

  if (!dragging) return null;

  const target = hoverTarget ?? (count <= 1 ? pageTarget : batchTarget(pageTarget));
  const landing = hoverTarget ? hoverFolder : folder;
  const into = landing ? ` into ${landing}` : "";
  const label =
    target.kind === "version"
      ? `Drop to add a new version of ${target.name}`
      : count > 1
        ? `Drop to upload ${count} files${into}`
        : `Drop to upload${into}`;

  return (
    <div className={styles.layer} data-target={target.kind} aria-hidden="true">
      <p className={styles.pill}>
        <Icon name="upload" />
        <span>{label}</span>
      </p>
    </div>
  );
}

export const markDropHandled = () => window.dispatchEvent(new Event("drive:drop-handled"));
